import React, { useState } from "react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  getDay,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
  format,
} from "date-fns";
import { useRouter } from "next/router";
import { useSession } from "@/components/providers/session";
import Link from "next/link";
import { getMonth } from "date-fns/getMonth";
import { currency } from "@/utils/formats";
import classNames from "@/utils/classnames";

export function createDates(
  start: Date,
  end: Date,
  selected: Date
): {
  date?: Date;
  previous: boolean;
  currentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}[] {
  let datesInMonth = eachDayOfInterval({
    start: startOfMonth(start),
    end: endOfMonth(end),
  }).map((x) => ({
    date: x,
    previous: false,
    currentMonth: isSameMonth(x, new Date()),
    isToday: isToday(x),
    isSelected: isSameDay(x, selected),
  }));
  let firstDate = getDay(datesInMonth[0].date);
  let emptyCells = firstDate === 0 ? 7 : firstDate;
  datesInMonth.unshift(
    // @ts-ignore
    ...Array.from(Array(emptyCells - 1)).map((x) => ({ previous: true }))
  );
  return datesInMonth;
}

export const isDateSelected = (arr: Date[], day): boolean => {
  return (
    arr &&
    arr.length > 0 &&
    arr?.map((date) => date?.toDateString()).includes(day?.date?.toDateString())
  );
};

async function loadEventData(date = new Date()) {
  const r = await fetch(`/api/courses/?date=${date.toISOString()}`);
  const s = await r.json();
  return s;
}

export default function CourseCalendar({ events, siteData, content }) {
  const [state, setState] = useState({
    selectedEvent: [],
    selectedType: null,
    current: new Date(),
    month: new Date(),
    imageUrl: null,
    selectedDate: null,
    allSelectedDates: [],
    monthEvents: events,
    days: createDates(
      startOfMonth(new Date()),
      endOfMonth(new Date()),
      new Date()
    ),
  });

  const router = useRouter();
  const session = useSession();

  const eventData = React.useMemo(
    () =>
      state.monthEvents && state.monthEvents.length > 0
        ? state.monthEvents.filter(
            (x) => x?.detail?.contentful_id === content?.sys?.id
          )
        : null,
    [state.monthEvents, content?.sys?.id]
  );

  const filteredEvents = React.useMemo(
    () =>
      state.monthEvents &&
      state.monthEvents.length > 0 &&
      state.monthEvents?.filter((event) => {
        const s =
          event.vtctSettings &&
          event.vtctSettings.dates &&
          event.vtctSettings.dates[0]
            ? event.vtctSettings.dates[0].start
            : event.date;

        return (
          new Date(s).toLocaleDateString() ===
          state?.selectedDate?.date.toLocaleDateString()
        );
      }),
    [state.monthEvents, state?.selectedDate]
  );

  const datesWithEvents = React.useMemo(
    () =>
      state &&
      state.days.length > 0 &&
      state.days.map((day) => {
        return {
          ...day,
          hasEvent: state.monthEvents?.some((event) => {
            const s =
              event.vtctSettings &&
              event.vtctSettings.dates &&
              event.vtctSettings.dates[0]
                ? event.vtctSettings.dates[0].start
                : event.date;

            return (
              new Date(s).toLocaleDateString() ===
              day?.date?.toLocaleDateString()
            );
          }),
          events: state.monthEvents?.filter((event) => {
            const s =
              event.vtctSettings &&
              event.vtctSettings.dates &&
              event.vtctSettings.dates[0]
                ? event.vtctSettings.dates[0].start
                : event.date;

            return (
              new Date(s).toLocaleDateString() ===
              day?.date?.toLocaleDateString()
            );
          }),
        };
      }),
    [state, router.pathname]
  );

  async function loadData(date = new Date()) {
    const r = await loadEventData(date);
    setState((s) => ({ ...s, monthEvents: r.events }));
    return r;
  }

  return (
    <div className="m-6 xl:mx-20 xl:my-10">
      <div className="md:grid md:grid-cols-10 md:gap-x-10">
        <div className="mt-10 text-center sm:col-start-3 sm:col-end-9 md:col-start-2 md:col-end-10 md:col-span-10 md:row-start-1 md:mt-9 lg:col-start-6 lg:col-end-13 landscape:lg:col-start-7 landscape:xl:col-start-6 landscape:xl:pl-12">
          <div className="flex items-center text-center text-gray-700">
            <button
              type="button"
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              onClick={() => {
                if (getMonth(new Date()) === getMonth(state.month)) {
                  return false;
                }
                setState((s) => {
                  loadData(subMonths(state.month, 1));
                  return {
                    ...s,
                    month: subMonths(state.month, 1),
                    days: createDates(
                      startOfMonth(subMonths(state.month, 1)),
                      endOfMonth(subMonths(state.month, 1)),
                      s.selectedDate
                    ),
                  };
                });
              }}
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="w-4 xl:w-5" aria-hidden="true" />
            </button>
            <div className="flex-auto font-semibold text-sm xl:text-base">
              {format(state.month, "MMMM")}
            </div>
            <button
              type="button"
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              onClick={() => {
                setState((s) => {
                  loadData(addMonths(state.month, 1));
                  return {
                    ...s,
                    month: addMonths(state.month, 1),
                    days: createDates(
                      startOfMonth(addMonths(state.month, 1)),
                      endOfMonth(addMonths(state.month, 1)),
                      s.selectedDate
                    ),
                  };
                });
              }}
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="w-4 xl:w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-2 grid grid-cols-7 text-center text-xxs xl:text-xs leading-6 text-gray-500">
            <div>M</div>
            <div>T</div>
            <div>W</div>
            <div>T</div>
            <div>F</div>
            <div>S</div>
            <div>S</div>
          </div>
          <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-100 text-xxs xl:text-xs shadow ring-1 ring-gray-200 overflow-hidden">
            {datesWithEvents &&
              datesWithEvents.length > 0 &&
              datesWithEvents?.map((day, dayIdx) => {
                let invalidDate =
                  day?.date?.getTime() < startOfDay(new Date()).getTime() ||
                  day?.date?.getTime() === undefined;
                return (
                  <button
                    key={
                      day && day.date
                        ? `-${dayIdx}-${day?.date?.toString()}`
                        : `date-${dayIdx}`
                    }
                    type="button"
                    disabled={invalidDate}
                    onClick={() => {
                      setState((s) => ({
                        ...s,
                        selectedDate: day,
                      }));
                    }}
                    className={classNames(
                      "px-0.5 py-1 xl:px-1 xl:py-3 hover:bg-gray-100 focus:z-10 text-xxs xl:text-sm relative",
                      day?.previous ? "bg-gray-50" : "bg-white",
                      (isDateSelected(state.allSelectedDates, day) ||
                        day?.isToday) &&
                        "font-semibold",
                      day?.date?.getTime() ===
                        state.selectedDate?.date?.getTime() && "bg-brand-50",
                      isDateSelected(state.allSelectedDates, day) &&
                        "text-white",

                      invalidDate && "cursor-not-allowed text-gray-400"
                    )}
                  >
                    {day &&
                      day.hasEvent &&
                      (state.selectedType === null ? (
                        <div className="flex absolute top-1">
                          <div
                            className={classNames(
                              " w-2 h-2 border p-0.5 rounded-full bg-pink-400",
                              day &&
                                day?.events.find((x) => {
                                  return (
                                    x?.detail?.attributes?.category === "VTCT"
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                          <div
                            className={classNames(
                              " w-2 h-2 border p-0.5 rounded-full bg-red-400",
                              day &&
                                day?.events.find((x) => {
                                  return (
                                    x?.detail?.attributes?.category === "Beauty"
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                          <div
                            className={classNames(
                              "w-2 h-2 border p-0.5 rounded-full bg-purple-700",
                              day &&
                                day.events.find((x) => {
                                  return (
                                    x?.detail.attributes.category ===
                                    "Permanent Makeup"
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                          <div
                            className={classNames(
                              "w-2 h-2 border p-0.5 rounded-full bg-blue-400",
                              day &&
                                day?.events.find((x) => {
                                  return (
                                    x?.detail?.attributes?.category ===
                                    "Injectables"
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                          <div
                            className={classNames(
                              "w-2 h-2 border p-0.5 rounded-full bg-green-400",
                              day &&
                                day.events.find((x) => {
                                  return (
                                    x?.detail?.attributes?.category ===
                                    "Package"
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                          <div
                            className={classNames(
                              "w-2 h-2 border p-0.5 rounded-full bg-yellow-600",
                              day &&
                                day.events.find((x) => {
                                  return (
                                    x?.detail?.attributes?.category ===
                                    "Aesthetics"
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                        </div>
                      ) : (
                        <div className="flex absolute top-1">
                          <div
                            className={classNames(
                              "w-2 h-2 border p-0.5 rounded-full",
                              state.selectedType === "VTCT" && "bg-pink-400",
                              state.selectedType === "Beauty" && "bg-red-400",
                              state.selectedType === "Permanent Makeup" &&
                                "bg-purple-700",
                              state.selectedType === "Injectables" &&
                                "bg-blue-400",
                              state.selectedType === "Package" &&
                                "bg-green-400",
                              state.selectedType === "Aesthetics" &&
                                "bg-yellow-600",
                              day &&
                                day?.events?.find((x) => {
                                  return (
                                    x?.detail?.attributes?.category ===
                                    state.selectedType
                                  );
                                })
                                ? ""
                                : "hidden"
                            )}
                          ></div>
                        </div>
                      ))}
                    <time
                      dateTime={day && day.date && day.date.toISOString()}
                      className={classNames(
                        "mx-auto flex items-center justify-center rounded-full py-1",
                        isDateSelected(state?.allSelectedDates, day) &&
                          "bg-brand-600"
                      )}
                    >
                      {day.date && format(day.date, "dd")}
                    </time>
                  </button>
                );
              })}
          </div>
          <div className="flex justify-evenly m-2 ">
            <div>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: "VTCT",
                  }));
                }}
                className="flex items-center text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-pink-400 border"></div>
                <p className="text-xs"> VTCT </p>
              </button>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: "Beauty",
                  }));
                }}
                className="flex items-center text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-red-400 border"></div>
                <p className="text-xs"> Beauty </p>
              </button>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: "Injectables",
                  }));
                }}
                className="flex items-center text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-blue-400 border"></div>
                <p className="text-xs"> Injectables </p>
              </button>
            </div>
            <div>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: "Package",
                  }));
                }}
                className="flex items-center text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-green-400 border"></div>
                <p className="text-xs"> Package </p>
              </button>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: "Aesthetics",
                  }));
                }}
                className="flex items-center text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-yellow-600 border"></div>
                <p className="text-xs"> Aesthetics </p>
              </button>
            </div>
            <div>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: "Permanent Makeup",
                  }));
                }}
                className="flex items-center justify-start text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-purple-700 border"></div>
                <p className="text-xs"> Permanent Makeup </p>
              </button>
              <button
                onClick={() => {
                  setState((s) => ({
                    ...s,
                    selectedType: null,
                  }));
                }}
                className="flex items-center justify-start text-center"
              >
                <div className="w-2 h-2 m-2 rounded-full bg-black border"></div>
                <p className="text-xs"> Show All </p>
              </button>
            </div>
          </div>
        </div>
        <ol className="mt-4 divide-y divide-gray-100 text-sm leading-6 sm:col-start-2 sm:col-end-10 lg:col-start-3 lg:col-end-9 lg:col-span-6 xl:col-span-5 landscape:lg:row-start-1 landscape:lg:col-start-1 landscape:lg:col-end-7 landscape:xl:col-end-6 xl:mt-8">
          <h2 className="text-lg font-semibold text-gray-900 md:mb-4 md:text-center lg:text-left mt-8 md:mt-0">
            Courses available on:{" "}
            {state?.selectedDate?.date?.toLocaleDateString()}
          </h2>
          {filteredEvents && filteredEvents.length > 0 ? (
            filteredEvents.map((event) => {
              const contentItem =
                content &&
                content.items?.find((x) => {
                  return x.sys.id === event?.detail.contentful_id;
                });

              console.log(contentItem?.fields?.url, "CONTENT ITEM");
              const url = contentItem?.fields?.url
                ? contentItem?.fields?.url &&
                  contentItem.fields.url.startsWith("/")
                  ? contentItem.fields.url
                  : `/${contentItem?.fields?.url}`
                : null;
              const full = event.seats - event.registrations.aggregate.count;
              return (
                <li
                  key={event.id}
                  className="relative sm:flex sm:space-x-6 pt-6 sm:static"
                >
                  {url &&
                  contentItem?.fields?.listingImage?.fields?.file.url ? (
                    <Link href={url}>
                      <img
                        src={
                          contentItem?.fields?.listingImage?.fields?.file.url
                        }
                        alt=""
                        className="flex-none rounded-lg object-cover sm:static sm:h-24 sm:w-24"
                      />
                    </Link>
                  ) : contentItem?.fields?.listingImage?.fields?.file.url ? (
                    <div>
                      <img
                        src={
                          contentItem?.fields?.listingImage?.fields?.file.url
                        }
                        alt=""
                        className="flex-none rounded-lg object-cover sm:static sm:h-24 sm:w-24"
                      />
                    </div>
                  ) : null}
                  <div className="mt-4 sm:mt-0 flex-auto sm:text-start sm:pr-0 sm:pb-2">
                    <h3 className="pr-10 text-base pt-1 font-semibold text-gray-900 sm:pr-0 flex justify-between">
                      {event.detail.title}
                      <span>{currency(event.price)}</span>
                    </h3>

                    {url ? (
                      <Link href={url} className="text-xs text-gray-600">
                        View course details
                      </Link>
                    ) : null}
                    <dl className="mt-2 flex flex-col text-gray-500 sm:flex-row">
                      <div className="flex flex-row-reverse sm:flex-row justify-end sm:justify-start items-center sm:space-x-3 flex-1 mb-4 sm:mb-0">
                        <div className="flex items-center ml-4 sm:ml-0">
                          <dt className="mt-0.5 mr-1">
                            <span className="sr-only">Date</span>
                            <CalendarIcon
                              className="h-4 w-4 text-gray-400"
                              aria-hidden="true"
                            />
                          </dt>
                          <dd className="sm:w-24">
                            <time dateTime={event.date} className="text-xs">
                              {format(new Date(event.start), "ha")} -{" "}
                              {format(new Date(event.end), "ha")}
                            </time>
                          </dd>
                        </div>
                        {event.tutors[0]?.user?.avatar[0]?.asset?.url &&
                        event.tutors[0]?.user?.avatar[0]?.asset?.url.length >
                          0 ? (
                          <div className="flex items-center">
                            <img
                              src={event.tutors[0]?.user?.avatar[0]?.asset?.url}
                              alt="Tutor"
                              className="flex h-6 w-6 self-center flex-none rounded-full mr-1 sm:mx-2"
                            />
                            <p>{event.tutors[0]?.user.name}</p>
                          </div>
                        ) : null}
                      </div>
                      <div className="flex justify-end sm:items-center mb-2 sm:mb-0 -mt-14 sm:mt-0">
                        {full > 0 ? (
                          <button
                            type="button"
                            onClick={() => {
                              session.addCourse({
                                ...content.items.find(
                                  (x) => x.sys.id === event.detail.contentful_id
                                ),
                                event: {
                                  id: event.detail.id,
                                  standardPrice: event.detail.price,
                                  contentful_id: event.detail.contentful_id,
                                  seat_price: event.price,
                                  elearning_course_id:
                                    event.detail.elearning_course_id,
                                  package: event.detail.package,
                                  events: [event],
                                  wasPrice: event.detail.wasPrice,
                                },
                                package: eventData && eventData.package,
                                selectedEvent: event,
                              });
                              router.push("/checkout");
                            }}
                            className="items-center justify-center px-6 py-2 m-0 border border-transparent rounded-md shadow text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 "
                          >
                            Book
                          </button>
                        ) : (
                          <p className="text-xs">Course Full</p>
                        )}
                      </div>
                    </dl>
                  </div>
                </li>
              );
            })
          ) : (
            <div className="text-center h-full pt-32">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {state.selectedDate
                  ? "No training courses scheduled for this date"
                  : "No date selected"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {state.selectedDate
                  ? "Select a different date to view courses"
                  : "Select a date to see the courses available"}
              </p>
            </div>
          )}
        </ol>
      </div>
    </div>
  );
}
